import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {warehouseGetAll, warehouseExcel} from '../../../redux/slices/product.slice'
import {useMemoizedStaticData} from '../../../utils/performanceOptimizations'

import {pagination} from '../../../helpers'

const selectProductData = (state) => ({
  warehouse: state.product.warehouse,
  loading: state.product.loading,
})

function List() {
  const intl = useIntl()
  const {warehouse, loading} = useSelector(selectProductData)
  const dispatch = useDispatch()

  const [params, setParams] = useState(pagination)

  useEffect(() => {
    if (params) dispatch(warehouseGetAll(params))
  }, [params, dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    if (params) {
      dispatch(
        warehouseExcel({
          params,
          fileName: intl.formatMessage({id: 'BC.WAREHOUSE'}),
        })
      )
    }
  }, [dispatch, params, intl])

  const Columns = useMemo(
    () => [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'WAREHOUSE.SEARCH.NAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'name',
        Cell: ({...props}) => (
          <CustomCellDouble
            item={props}
            double='isDefaultWareHouse'
            badge={intl.formatMessage({id: 'WAREHOUSE.SEARCH.ISDEFAULTWAREHOUSE'})}
          />
        ),
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'WAREHOUSE.SEARCH.PROVINCENAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'provinceName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'WAREHOUSE.SEARCH.DISTRICTNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'districtName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
            className='min-w-125px'
          />
        ),
        accessor: 'lastModifiedBy',
        Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
      },
    ],
    [intl, handleSorting]
  )

  const actionButtons = useMemoizedStaticData(
    () => [
      {
        title: 'BUTTON.EXCEL',
        onClick: handleExcelExport,
        icon: '/images/icons/duotune/arrows/arr044.svg',
        variant: 'light-primary',
      },
    ],
    [handleExcelExport]
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.WAREHOUSE'})}</PageTitle>
      <KTCard>
        <ListHeader search={handleSearch} actionButtons={actionButtons} />
        <KTCardBody>
          {!warehouse && <ListLoading />}
          {warehouse && (
            <Table items={warehouse} cols={Columns} handlePagination={handlePagination} paddless />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
